import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RefreshCcw, List } from "lucide-react";
import { TOKEN_KEY } from "@/pages/AdminLogin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLUMNS = [
  { id: "neu", label: "Neu", color: "#E8E5D0" },
  { id: "kontaktiert", label: "Kontaktiert", color: "#B8CFA0" },
  { id: "qualifiziert", label: "Qualifiziert", color: "#B8CFA0" },
  { id: "termin_vereinbart", label: "Termin", color: "#A0C87B" },
  { id: "angebot_versendet", label: "Angebot", color: "#A0C87B" },
  { id: "gewonnen", label: "Gewonnen", color: "#7FB960" },
  { id: "verloren", label: "Verloren", color: "#E27D7D" },
];

const AdminPipeline = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  const api = useMemo(() => axios.create({
    baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {},
  }), [token]);

  const fetch = async () => {
    if (!token) { navigate("/admin/login", { replace: true }); return; }
    setLoading(true);
    try { const r = await api.get("/leads?limit=500"); setLeads(r.data.filter(l => !l.archived)); }
    catch (e) { if ([401, 403].includes(e?.response?.status)) navigate("/admin/login", { replace: true }); }
    finally { setLoading(false); }
  };
  useEffect(() => { document.title = "CRM · Pipeline"; fetch(); }, []); // eslint-disable-line

  const onDrop = async (colId) => {
    if (!dragId) return;
    const lead = leads.find(l => l.id === dragId);
    if (!lead || lead.status === colId) { setDragId(null); return; }
    const prev = leads;
    setLeads(leads.map(l => l.id === dragId ? { ...l, status: colId } : l));
    setDragId(null);
    try { await api.patch(`/leads/${dragId}`, { status: colId }); toast.success("Status aktualisiert."); }
    catch { setLeads(prev); toast.error("Fehlgeschlagen."); }
  };

  return (
    <div className="min-h-screen bg-[#5C6B49] text-[#F3F1E8] py-8 px-4 sm:px-6" data-testid="admin-pipeline-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/admin/leads" className="inline-flex items-center gap-2 text-[#C9C6B4] hover:text-[#F3F1E8] text-xs font-bold uppercase tracking-wider mb-2">
              <ArrowLeft size={12}/> Leadliste
            </Link>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Pipeline</h1>
            <p className="text-[#A8A58F] text-sm mt-1">{leads.length} aktive Leads · Drag & Drop</p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/leads" className="inline-flex items-center gap-2 border border-white/15 hover:bg-black/[0.15] text-sm font-bold rounded-full px-4 py-2"><List size={14}/> Liste</Link>
            <button onClick={fetch} className="inline-flex items-center gap-2 border border-white/15 hover:bg-black/[0.15] text-sm font-bold rounded-full px-4 py-2"><RefreshCcw size={14}/> Neu laden</button>
          </div>
        </div>

        {loading ? <div className="text-[#C9C6B4]">Lade…</div> : (
          <div className="flex gap-3 overflow-x-auto pb-4">
            {COLUMNS.map(col => {
              const colLeads = leads.filter(l => l.status === col.id);
              return (
                <div key={col.id}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("ring-2","ring-[#E8E5D0]/60"); }}
                  onDragLeave={e => e.currentTarget.classList.remove("ring-2","ring-[#E8E5D0]/60")}
                  onDrop={e => { e.currentTarget.classList.remove("ring-2","ring-[#E8E5D0]/60"); onDrop(col.id); }}
                  className="min-w-[280px] w-[280px] bg-[#4E5A3B]/50 rounded-2xl p-3 flex flex-col"
                  data-testid={`col-${col.id}`}>
                  <div className="flex items-center justify-between mb-3 px-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{background: col.color}} />
                      <span className="text-xs font-bold uppercase tracking-wider text-[#F3F1E8]">{col.label}</span>
                    </div>
                    <span className="text-xs text-[#A8A58F]">{colLeads.length}</span>
                  </div>
                  <div className="space-y-2 min-h-[100px]">
                    {colLeads.map(l => (
                      <div key={l.id} draggable onDragStart={() => setDragId(l.id)} onDragEnd={() => setDragId(null)}
                        className={`bg-[#5C6B49] border border-white/12 rounded-lg p-3 cursor-move hover:border-[#E8E5D0]/40 transition ${dragId === l.id ? 'opacity-40' : ''}`}
                        data-testid={`card-${l.id}`}>
                        <div className="font-bold text-sm text-[#F3F1E8]">{l.first_name} {l.last_name}</div>
                        <div className="text-[11px] text-[#C9C6B4] mt-0.5 truncate">{l.email}</div>
                        <div className="text-[10px] text-[#8F8C7A] mt-1">{new Date(l.created_at).toLocaleDateString("de-DE")} · {l.city}</div>
                      </div>
                    ))}
                    {colLeads.length === 0 && <div className="text-[11px] text-[#8F8C7A] italic text-center py-4">leer</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPipeline;
